import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { gql } from 'graphql-request'
import { userStore, settingsStore } from '@/stores'
import { authorizedGraphQLRequest } from '@/api'
import Module2Page from '@/pages/Module2/Module2Page'

const GET_USER_MODULES = gql`
  query getUserModules {
    getUserModules {
      id
      name
    }
  }
`

const OrganizationBasedRoute: React.FC = () => {
  const { isLoading } = userStore
  const { module, settingsLoaded } = settingsStore
  const [autoAssigning, setAutoAssigning] = useState(false)
  const [noModulesAvailable, setNoModulesAvailable] = useState(false)

  useEffect(() => {
    if (!settingsLoaded || isLoading || typeof module === 'number') return

    const autoAssignModule = async () => {
      setAutoAssigning(true)
      try {
        const data = await authorizedGraphQLRequest<{ getUserModules: { id: number; name: string }[] }>(GET_USER_MODULES)
        const modules = data.getUserModules || []
        if (modules.length > 0) {
          await settingsStore.updateSettings({ module: modules[0].id })
        } else {
          setNoModulesAvailable(true)
        }
      } catch (err) {
        console.error('Failed to auto-assign module:', err)
        setNoModulesAvailable(true)
      } finally {
        setAutoAssigning(false)
      }
    }

    autoAssignModule()
  }, [settingsLoaded, isLoading, module])

  if (isLoading || !settingsLoaded || autoAssigning) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  // If module is a numeric ID (user has saved a preference), show Module2Page
  if (typeof module === 'number') {
    return <Module2Page />
  }

  // No modules available at all — prompt user to contact admin
  if (noModulesAvailable) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-6">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800">No module selected</h2>
          <p className="text-gray-500 text-sm">Go to Settings &rarr; Preferences to choose your module, or contact your administrator if no modules are available.</p>
          <a href="/settings" className="mt-2 px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors">
            Go to Settings
          </a>
        </div>
      </div>
    )
  }

  return null
}

export default observer(OrganizationBasedRoute)
